import {
    DEFAULT_TITLE,
    TYPE_AUTHOR,
    TYPE_DETAIL,
    TYPE_LANDING,
    TYPE_SHOW,
} from "./constants";

/**
 * Build the required data object to pass to analytics for Detail pages
 *
 * @param {object} props the page props
 * @param {object} props.author the author data of the story
 * @param {string} props.baseSection the base section the story belongs to
 * @param {object} props.analytics the data specifically for analytics
 * @param {object} props.content the data specific to the story
 *
 * @returns {object} the structured analytics data
 */
export const getDetailAnalytics = (props) => {
    if (!props) {
        return;
    }

    const { content = {} } = props;

    const baseObj = {
        authors: getAuthors({
            authorList: content.authorList,
            byline: content.byline,
        }),
        contentarea: content.contentarea,
        contentid: content.id,
        contenttype: content.type,
        department: content.attribution,
        embedtypes: content.embedtypes,
        keywords: content.keywords,
        publishedTime: content.publishedAt,
        sponsor: getSponsor(content.sponsorBio),
        subsection1: content.subsection1,
        subsection2: content.subsection2,
        subsection3: content.subsection3,
        subsection4: content.subsection4,
        title: content.headline,
        updatedTime: content.updatedAt,
        url: content.url,
        wordcount: content.wordcount,
        grapeshotCategories: content.gs_categories,
        grapeshotKeywords: content.gs_keywords,
    };

    return cleanObject(baseObj);
};

// https://cbc-digital.atlassian.net/wiki/spaces/WPP/pages/edit-v2/824377370?draftShareId=f7ec6874-4db5-4813-ac25-53afedaad455#CBC.APP.SC.DTM.DATA
export const getLiveStoryAnalytics = (content) => {
    if (!content) return;

    const tracking = content.section?.tracking ?? {};
    const analyticsProps = {
        contentarea: tracking.contentArea,
        contentid: content.sourceId,
        contenttype: content.type,
        //embedtypes: content.embedtypes,
        keywords: {
            tags: content.tags,
            concepts: content.concepts,
        },
        publishedTime: content.publishedAt,
        subsection1: tracking.subsection1,
        subsection2: tracking.subsection2,
        subsection3: tracking.subsection3,
        subsection4: tracking.subsection4,
        title: content.title,
        updatedTime: content.updatedAt,
        url: content.url,
        //wordcount: content.wordcount,
        //grapeshotCategories: content.gs_categories,
        //grapeshotKeywords: content.gs_keywords,
    };

    return cleanObject(analyticsProps);
};

/**
 * Build the required data object to pass to analytics for Landing pages
 *
 * @param {Object} Object containing the analytics and meta keys
 * @param {Object} props.analytics
 * @param {Object} props.meta
 *
 * @returns {Object} the structured analytics data
 */
export const getLandingAnalytics = (props) => {
    if (!props) {
        return;
    }
    const { meta = {}, analytics = {} } = props;
    const { tracking = {} } = analytics;

    const baseObj = {
        contentarea: tracking.contentarea,
        contenttype: tracking.contenttype,
        subsection1: tracking.subsection1,
        subsection2: tracking.subsection2,
        subsection3: tracking.subsection3,
        subsection4: tracking.subsection4,
        title: tracking.title,
        url: meta.canonical,
        department: tracking.department,
    };

    return cleanObject(baseObj);
};

export const getAuthorBioAnalytics = (props) => {
    if (!props) {
        return;
    }

    const {
        meta = {},
        pageTitle = "",
        content = {},
        contentId = "",
        author = {},
    } = props;

    const baseObj = {
        authors: author.name,
        contentid: contentId,
        contenttype: content.contentType,
        title: pageTitle,
        url: meta.canonical,
    };

    return cleanObject(baseObj);
};

/**
 * The unfortunate reason this has to exist is that analytics requires state from multiple stores.
 * These dispatches happen asynchronously, so it's non-deterministic what order they'll return.
 * This predicate function acts as a gate for the necessary pieces of state that may not exist when
 * their respective components mount. This should be refactored once actions are cleaned up.
 *
 * Landing pages -> check title and contenttype
 * Show pages -> check title
 * Detail + Author pages -> good to go
 *
 * @param {object} analytics the analytics object
 * @param {bool} isLanding whether the page is landing or detail
 */
export const checkAnalyticsLoaded = (analytics, type) => {
    if (!analytics) return;

    if (type === TYPE_LANDING) {
        if (analytics.contenttype && analytics.title !== DEFAULT_TITLE) {
            return true;
        }
    } else if (type === TYPE_SHOW) {
        if (analytics.title && analytics.title !== DEFAULT_TITLE) {
            return true;
        }
    } else if (type === TYPE_DETAIL || type === TYPE_AUTHOR) {
        return true;
    }

    return false;
};

/**
 * Structure the author field for analytics data
 *
 * @param {object} author the Author record for the story
 * @param {string} byline the author byline
 *
 * @returns {string} the author + byline
 */
export const getAuthors = ({ authorList, byline } = {}) => {
    let authorNames = authorList
        ? authorList
              .reduce((names, author) => {
                  names.push(author.name);
                  return names;
              }, [])
              .join(",")
        : undefined;

    if (byline) {
        authorNames = authorNames ? `${authorNames},${byline}` : byline;
    }

    return authorNames;
};

/**
 * Structure the sponsor object for analytics data
 *
 * @param {object} sponsor data for the sponsor
 *
 * @returns {object} the structured sponsor info
 */
export const getSponsor = (sponsor) => {
    if (!sponsor || !sponsor.name) {
        return;
    }
    return {
        name: sponsor.name,
    };
};

/**
 * Take an object and clean all non-valid fields (null, undefined, falsy, empty object/array)
 *
 * @param {object} obj the object to clean
 *
 * @returns {object} a cleaned version of the original object
 */
export const cleanObject = (obj) => {
    if (!obj || obj.constructor.name !== "Object") {
        return;
    }
    return Object.keys(obj).reduce((acc, cur) => {
        const item = obj[cur];

        // We don't want to clean "false" bool vals (they are a valid property)
        if (item || typeof item === "boolean") {
            // Clean empty arrays && objects
            if (Array.isArray(item) && item.length === 0) {
                return acc;
            } else if (
                item.constructor.name === "Object" &&
                Object.keys(item).length === 0
            ) {
                return acc;
            }
            acc[cur] = obj[cur];
        }
        return acc;
    }, {});
};

/**
 * This keeps track of the a trackingCounter to use for testing analytics
 * Close around counter and return incremented count every time function is called
 *
 */
export const getTrackingCounter = (() => {
    let trackingCounter = 0;

    return () => {
        return ++trackingCounter;
    };
})();

/**
 * This keeps track of the last url that was used to fire analytics. Because the url comes
 * from the Redux store, sometimes it updates after the component has already mounted, causing
 * analytics to fire twice. This function acts as a predicate to whether analytics should fire,
 * checking the url that was last used, and updating it when it's changed
 *
 */
const hasLocationChanged = () => {
    let prevUrl = "";

    return (newUrl) => {
        if (newUrl && newUrl !== prevUrl) {
            prevUrl = newUrl;
            return true;
        }
        return false;
    };
};

export const sanitizeLinkTracking = (str) => {
    if (!str) {
        return;
    }

    if (str.includes("sclt-")) {
        const tempStr = str.replace("sclt-", "").replace(/[^a-zA-Z0-9]/g, "");
        return `sclt-${tempStr}`;
    }

    return str.replace(/[^a-zA-Z0-9]/g, "");
};

/**
 * This is the function used to set up the hasLocationChanged predicate
 *
 */
export const locationChanged = hasLocationChanged();
